/// <amd-module name="Wit/Common/WorkItemDialogs/Components/SelectTestItemForm" />
import * as React from "react";
import { getTFSProject } from "Tfs/Platform/Page";
import { useObservable, useObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { usePageContext } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { FormItem } from "VSSUI/FormItem";
import { Icon, IconSize } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { ListSelection, renderListCell } from "VSSUI/List";
import { Spinner } from "VSSUI/Spinner";
import { ITableColumn, SimpleTableCell, Table } from "VSSUI/Table";
import { TextField } from "VSSUI/TextField";
import { ISelectionRange } from "VSSUI/Utilities/Selection";
import { WorkItem, WorkItemFieldReference } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { getWorkItemTrackingClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { AreaPathDropdown } from "Wit/Common/WorkItemControls/AreaPathDropdown";
import * as Resources from "Wit/Common/WorkItemDialogs/Resources";
import { getIconDataObservable } from "Wit/UI/Services/WorkItemTypeIconMetadataService";
import { WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";
import { IWorkItemService } from "Wit/WorkItem/Service/Contracts";

interface ISelectTestItemFormProps {
    testItemType: string;
    multipleSelect?: boolean;
    onMultiSelectionChanged?(workItemIds: number[]): void;
    onSelectionChanged?(workItem: WorkItem): void;
}

interface IWiqlResult {
    columns: WorkItemFieldReference[];
    workItems: WorkItem[];
}

interface ISelectedTestPlanPanelState {
    columns?: Map<string, string>;
    loading: boolean;
}

const FieldsToFetch = [
    CoreFieldRefNames.Id,
    CoreFieldRefNames.WorkItemType,
    CoreFieldRefNames.Title,
    CoreFieldRefNames.AreaPath,
    CoreFieldRefNames.IterationPath
];

const TestItemWiqlWithoutTitle =
    "SELECT [System.Id], [System.WorkItemType], [System.Title], [System.AreaPath], [System.IterationPath] FROM WorkItems WHERE ([System.TeamProject] = @project AND [System.WorkItemType] IN GROUP '{0}' AND [System.AreaPath] UNDER '{1}')";

const TestItemWiqlWithTitle =
    "SELECT [System.Id], [System.WorkItemType], [System.Title], [System.AreaPath], [System.IterationPath] FROM WorkItems WHERE ([System.TeamProject] = @project AND [System.WorkItemType] IN GROUP '{0}' AND [System.AreaPath] UNDER '{1}' AND [System.Title] CONTAINS '{2}')";

export const SelectTestItemForm: React.FunctionComponent<ISelectTestItemFormProps> = ({
    testItemType,
    multipleSelect,
    onSelectionChanged,
    onMultiSelectionChanged
}) => {
    const pageContext = usePageContext();
    const projectId = getTFSProject(pageContext)?.id ?? "";

    const [titleValue] = useObservable("");
    const [areaPathValue] = useObservable("");
    const [items] = useObservableArray<WorkItem>([]);
    const [state, setState] = React.useState<ISelectedTestPlanPanelState>({ loading: false });
    const selection = React.useRef(new ListSelection({ multiSelect: multipleSelect }));

    const titleRenderer = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<WorkItem>, workItem: WorkItem) => {
        const workItemType = workItem.fields[CoreFieldRefNames.WorkItemType];
        const title = workItem.fields[CoreFieldRefNames.Title];
        const iconData = getIconDataObservable(projectId, workItemType, pageContext);
        return (
            <SimpleTableCell
                className={tableColumn.className}
                columnIndex={columnIndex}
                key={`results-${tableColumn.id}-${rowIndex}`}
                tableColumn={tableColumn}
            >
                <WorkItemTypeIcon className="icon-margin" data={iconData} />
                <Link
                    className="text-ellipsis"
                    tooltipProps={{ overflowOnly: true }}
                    onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.KeyboardEvent<HTMLAnchorElement>) => {
                        const witService = pageContext.getService<IWorkItemService>("work-item-service");
                        witService.openWorkItem(Number(workItem.fields[CoreFieldRefNames.Id]));
                    }}
                >
                    {title}
                </Link>
            </SimpleTableCell>
        );
    };

    const onRunWiql = async () => {
        setState({ loading: true });
        const { columns, workItems } = await runTestPlanSearch(pageContext, projectId, testItemType, areaPathValue.value, titleValue.value);
        const columnsMap = new Map<string, string>();
        for (const column of columns) {
            columnsMap.set(column.referenceName, column.name);
        }

        setState({ columns: columnsMap, loading: false });
        items.value = workItems;
    };

    const getGenericColumn = (
        columnId: string,
        columnsMap: Map<string, string>,
        fieldReferenceName: string,
        width: number
    ): ITableColumn<WorkItem> => ({
        id: columnId,
        name: columnsMap.get(fieldReferenceName),
        renderCell: (rowIndex, columnIndex, tableColumn, tableItem) =>
            genericRenderer(rowIndex, columnIndex, tableColumn, tableItem, tableItem.fields[fieldReferenceName]),
        width
    });

    const getColumns = (columnsMap: Map<string, string>): ITableColumn<WorkItem>[] => [
        getGenericColumn("id-column", columnsMap, CoreFieldRefNames.Id, -15),
        getGenericColumn("type-column", columnsMap, CoreFieldRefNames.WorkItemType, -20),
        {
            id: "title-column",
            name: columnsMap.get(CoreFieldRefNames.Title),
            renderCell: titleRenderer,
            width: -25
        },
        getGenericColumn("area-path-column", columnsMap, CoreFieldRefNames.AreaPath, -20),
        getGenericColumn("iteration-column", columnsMap, CoreFieldRefNames.IterationPath, -20)
    ];

    return (
        <div className="flex-column flex-grow rhythm-vertical-8">
            <FormItem
                label={
                    <span className="flex-row">
                        {Resources.TitleLabel}
                        <Icon className="margin-left-4" iconName="Info" size={IconSize.small} tooltipProps={{ text: Resources.TitleSearchTooltip }} />
                    </span>
                }
            >
                <TextField onChange={(event, value) => (titleValue.value = value)} value={titleValue} />
            </FormItem>
            <FormItem label={Resources.AreaPathSearchLabel}>
                <AreaPathDropdown
                    onSetPath={(areaPath: string) => (areaPathValue.value = areaPath)}
                    selectDefaultValue
                    selectedText={areaPathValue}
                />
            </FormItem>
            <span className="flex-row">
                <Button iconProps={{ iconName: "Search" }} onClick={onRunWiql} primary text={Resources.Search} />
                {state.loading && <Spinner className="margin-left-8" />}
            </span>
            {state.columns && (
                <Table
                    columns={getColumns(state.columns)}
                    itemProvider={items}
                    onSelect={(event, row) => {
                        if (multipleSelect) {
                            const selectedIds = getItemsFromRange(selection.current.value, items.value);
                            onMultiSelectionChanged && onMultiSelectionChanged(selectedIds);
                        } else {
                            onSelectionChanged && onSelectionChanged(row.data);
                        }
                    }}
                    selection={selection.current}
                />
            )}
        </div>
    );
};

function genericRenderer(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<WorkItem>, tableItem: WorkItem, value: string) {
    return (
        <SimpleTableCell
            className={tableColumn.className}
            columnIndex={columnIndex}
            key={`results-${tableColumn.id}-${rowIndex}`}
            tableColumn={tableColumn}
        >
            {renderListCell(value)}
        </SimpleTableCell>
    );
}

async function runTestPlanSearch(
    pageContext: IVssPageContext,
    projectId: string,
    testItemType: string,
    areaPath: string,
    title?: string
): Promise<IWiqlResult> {
    const witClient = getWorkItemTrackingClient(pageContext);
    const wiql = {
        query: title ? format(TestItemWiqlWithTitle, testItemType, areaPath, title) : format(TestItemWiqlWithoutTitle, testItemType, areaPath)
    };

    const { workItems, columns } = await witClient.queryByWiql(wiql, projectId, undefined, undefined, 1000);
    const idsToFetch = workItems.map(item => item.id);
    const service = pageContext.getService<IWorkItemService>("work-item-service");
    const fullWorkItems = (await Promise.all(service.loadWorkItems(projectId, idsToFetch, FieldsToFetch))).flat();
    return {
        columns,
        workItems: fullWorkItems
    };
}

function getItemsFromRange(ranges: ISelectionRange[], items: WorkItem[]) {
    const selectedIds: Set<number> = new Set();
    for (const range of ranges) {
        const beginIndex = range.beginIndex;
        const endIndex = range.endIndex;

        for (let i = beginIndex; i <= endIndex; i++) {
            selectedIds.add(items[i].id);
        }
    }

    return Array.from(selectedIds);
}
